import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui';
import { Button, Input, Card } from '../components/ui';
import './Login.css';
import logo from '../assets/images/logo.png'

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  // Redirect if already authenticated
  if (isAuthenticated && !authLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);

    try {
      const success = await login(data.email, data.password, data.rememberMe);

      if (success) {
        addToast({
          type: 'success',
          title: t('auth.loginButton'),
          message: t('dashboard.welcome')
        });
        navigate('/dashboard', { replace: true });
      } else {
        setError('root', {
          type: 'manual',
          message: t('auth.invalidCredentials')
        });
        addToast({
          type: 'error',
          title: t('notifications.error'),
          message: t('auth.invalidCredentials')
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      addToast({
        type: 'error',
        title: t('notifications.error'),
        message: t('notifications.networkError')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName: keyof LoginFormData) => {
    const error = errors[fieldName];
    if (!error) return undefined;

    switch (error.type) {
      case 'required':
        return t(`auth.${fieldName}Required`);
      case 'pattern':
        return fieldName === 'email' ? t('auth.emailInvalid') : undefined;
      default:
        return error.message;
    }
  };

  if (authLoading) {
    return (
      <div className="login-loading">
        <div className="login-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-pattern"></div>
      </div>

      <div className="login-content">
        <Card className="login-card" padding="lg" shadow="lg">
          <div className="login-header">
            <div className="login-logo">
              <div className="logo-icon">
                <img
                  src={logo}
                  alt="Dawaak Logo"
                  width="48"
                  height="48"
                  style={{ borderRadius: '50%' }}
                  onError={(e) => {
                    // Fallback to a simple medical cross if PNG is not found
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.setAttribute('style', 'display: block');
                  }}
                />
                {/* Fallback SVG - medical cross */}
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ display: 'none' }}
                >
                  <circle cx="12" cy="12" r="10" fill="#4A90E2" />
                  <rect x="11" y="7" width="2" height="10" fill="white" />
                  <rect x="7" y="11" width="10" height="2" fill="white" />
                </svg>
              </div>
              <h1 className="logo-text">Dawaak CMS</h1>
            </div>

            <div className="login-title">
              <h2>{t('auth.loginTitle')}</h2>
              <p>{t('auth.loginSubtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form" noValidate>
            {errors.root && (
              <div className="login-error" role="alert">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {errors.root.message}
              </div>
            )}

            <Input
              label={t('auth.email')}
              type="email"
              fullWidth
              error={getFieldError('email')}
              leftIcon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              }
              {...register('email', {
                required: true,
                pattern: {
                  value: /^[A-Z0-9._%+-]+(@[A-Z0-9.-]+\.[A-Z]{2,})?$/i,
                  message: t('auth.emailInvalid')
                }
              })}
            />

            <Input
              label={t('auth.password')}
              type="password"
              fullWidth
              error={getFieldError('password')}
              leftIcon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <circle cx="12" cy="16" r="1" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
              {...register('password', {
                required: true,
                minLength: {
                  value: 6,
                  message: t('forms.minLength', { min: 6 })
                }
              })}
            />

            {/* <div className="login-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  {...register('rememberMe')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">{t('auth.rememberMe')}</span>
              </label> */}

              {/* <button type="button" className="forgot-password-link">
                {t('auth.forgotPassword')}
              </button> 
               </div> */}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('auth.loginButton')}
            </Button>
          </form>

          {/* <div className="login-footer">
            <div className="demo-credentials">
              <p className="demo-label">Demo Credentials:</p>
              <p className="demo-text">Email: admin@dawaak.com</p>
              <p className="demo-text">Password: admin123</p>
            </div>
          </div> */}
        </Card>
      </div>
    </div>
  );
};

export default Login;